import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckSquare,
  Award,
  Trophy,
  Target,
  Users,
  Settings as SettingsIcon,
  Plus,
  Save,
  Trash2,
  Edit,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Info,
  CheckCircle,
  Circle,
  Clock,
  Package,
  AlertCircle,
  X,
  Lightbulb,
  Filter,
  Search,
  Eye,
  FileDown,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { ProgressionCard as SharedProgressionCard } from '../components/MerkeskytingProgressionCard';
import BadgeIcon from '../components/BadgeIcon';
import NumericKeypad from '../components/NumericKeypad';
import { fmtDateNo } from '../utils/dateFormat';
import ScrollableTabBar from '../components/ScrollableTabBar';
import DiplomaManager from '../components/DiplomaManager';
import MerkeRegisterEntryCard, { BulkSeriesBar, resizeScores } from '../components/MerkeRegisterEntryCard';
import CollapsibleInfoTip from '../components/CollapsibleInfoTip';
import { FlipbookModal, FlipbookTrigger } from '../components/FlipbookModal';
import MerkeChangeLog from '../components/MerkeChangeLog';
import MerkeBulkBar from '../components/MerkeBulkBar';
import MerkeRegisterSaveBar, { SaveButton, SyncStatus } from '../components/MerkeRegisterSaveBar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Badge visuals
const LEVEL_STYLES = {
  nybegynner: { color: '#F97316', bg: 'bg-orange-500', text: 'text-orange-500', ring: 'ring-orange-500/40', label: 'Nybegynner' },
  viderekommende: { color: '#0F172A', bg: 'bg-slate-800', text: 'text-slate-300', ring: 'ring-slate-500/40', label: 'Viderekommende' },
  bronse: { color: '#B45309', bg: 'bg-amber-700', text: 'text-amber-500', ring: 'ring-amber-500/40', label: 'Bronse' },
  solv: { color: '#94A3B8', bg: 'bg-slate-400', text: 'text-slate-300', ring: 'ring-slate-300/40', label: 'Sølv' },
  gull: { color: '#EAB308', bg: 'bg-yellow-500', text: 'text-yellow-400', ring: 'ring-yellow-500/40', label: 'Gull' },
};

const InfoTip = ({ children }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm" data-testid="info-tip">
    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
    <div>{children}</div>
  </div>
);

const TipCard = ({ children }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-100 text-sm" data-testid="tip-card">
    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-yellow-400" />
    <div>{children}</div>
  </div>
);

const draftKey = (user) => `merke_register_draft:${user?.id || 'anon'}:${user?.organization_id || ''}`;
const readDraft = (user) => {
  try { return JSON.parse(localStorage.getItem(draftKey(user)) || 'null'); } catch { return null; }
};

// Bruker det offisielle PNG-ikonet, samme navn for bakoverkompatibilitet.
const BadgeRing = ({ level, size = 40 }) => (
  <BadgeIcon level={level} size={size} />
);

const Merkeskyting = () => {
  const { user, isOrgAdmin, isRangeOfficer, isSuperAdmin, isAdmin } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canManage = isAdmin || isOrgAdmin || isRangeOfficer || isSuperAdmin;

  const [registerResetSignal, setRegisterResetSignal] = useState(0);
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [tab, setTab] = useState(() => (readDraft(user)?.entries?.length ? 'registrer' : 'oversikt')); // oversikt|registrer|medlemmer|utdeling|innstillinger
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [cfg, st] = await Promise.all([
          axios.get(`${API_BASE}/api/merkeskyting/config`, { headers }),
          axios.get(`${API_BASE}/api/merkeskyting/settings`, { headers }),
        ]);
        setConfig(cfg.data);
        setSettings(st.data);
        if (canManage) {
          const dr = await axios.get(`${API_BASE}/api/merkeskyting/register-draft`, { headers }).catch(() => null);
          if (dr?.data?.draft?.entries?.length) setTab('registrer');
        }
      } catch (e) {
        console.error(e);
        toast.error('Kunne ikke laste Merkeskyting');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <div className="p-8 text-center text-gray-400" data-testid="merkeskyting-loading">Laster...</div>;
  }

  if (!settings?.enabled && !canManage) {
    return (
      <div className="p-8 text-center text-gray-400" data-testid="merkeskyting-disabled">
        <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p>Merkeskyting er ikke aktivert for din klubb ennå.</p>
      </div>
    );
  }

  const tabs = canManage
    ? [
        { id: 'oversikt', label: 'Oversikt', icon: Trophy },
        { id: 'registrer', label: 'Registrer', icon: Plus },
        { id: 'resultater', label: 'Resultater', icon: Target },
        { id: 'medlemmer', label: 'Medlemmer', icon: Users },
        { id: 'utdeling', label: 'Utdeling', icon: Award },
        { id: 'historikk', label: 'Historikk', icon: Clock },
        { id: 'innstillinger', label: 'Innstillinger', icon: SettingsIcon },
      ]
    : [{ id: 'mine', label: 'Mine merker', icon: Award }];

  const activeTab = canManage ? tab : 'mine';

  return (
    <div className="space-y-4 pb-24" data-testid="merkeskyting-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/club-operations')}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300"
            data-testid="merkeskyting-back-btn"
            title="Tilbake til Klubb drift"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-7 h-7 text-yellow-500" />
              Merkeskyting
            </h1>
            <p className="text-gray-400 text-sm">NSFs ordning for ferdighetsmerker – digital protokoll</p>
          </div>
        </div>
      </div>

      {/* Info-box om ordningen + nedlasting av regler-PDF */}
      {activeTab === 'oversikt' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="merkeskyting-overview-top">
          <div className="flex flex-col gap-3">
          <CollapsibleInfoTip prefKey="overview_howitworks" title="Slik fungerer merkeskyting" mobileCollapsed>
            Skytteren må oppnå merkekravet <strong>10 ganger</strong> for hvert nivå. Progresjonen går sekvensielt: Nybegynner → Viderekommende → Bronse → Sølv → Gull. Det er <strong>ingen tidsbegrensning</strong> – resultater teller på tvers av år.
          </CollapsibleInfoTip>
          <CollapsibleInfoTip prefKey="overview_rules_pdf" title="Regler og veiledning (PDF)" variant="emerald" icon={FileDown} mobileCollapsed>
            Last ned den offisielle NSF-veiledningen for Merkeskyting – regler, poengkrav og praktisk gjennomføring. Del gjerne dokumentet med skyteledere og medlemmer.
            <a
              href="https://d1nx7cbbklnffs.cloudfront.net/public/merkeskyting/Aktivlogg_Merkeskyting_Regler_og_veiledning.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              className="block mt-1 text-xs text-emerald-300 underline hover:text-emerald-100"
              data-testid="merkeskyting-rules-download"
            >
              Klikk her for å laste ned →
            </a>
          </CollapsibleInfoTip>
          </div>
          <FlipbookTrigger onOpen={() => setShowFlipbook(true)} />
        </div>
      )}
      {showFlipbook && <FlipbookModal onClose={() => setShowFlipbook(false)} />}

      {/* Ikke aktivert varsel for admin */}
      {!settings?.enabled && canManage && (
        <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/40 text-yellow-100 flex items-start gap-2" data-testid="not-enabled-banner">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-400" />
          <div className="flex-1">
            <p className="font-semibold">Modulen er ikke aktivert</p>
            <p className="text-sm mt-1">Gå til <button className="underline" onClick={() => navigate('/admin?tab=settings')}>Skyteleder → Innstillinger</button> for å aktivere Merkeskyting.</p>
          </div>
        </div>
      )}

      {/* Tab-navigasjon (scrollable-pill-bar med piler + touch-swipe) */}
      <div className="mb-2" data-testid="merkeskyting-tab-bar-wrap">
        <ScrollableTabBar
          className="gap-2 md:gap-3 -mx-1 px-2 pt-2 pb-2 md:pb-1"
          testId="merkeskyting-tab-bar"
        >
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { if (t.id === 'registrer' && activeTab === 'registrer') setRegisterResetSignal(n => n + 1); setTab(t.id); }}
                className={`snap-start shrink-0 flex flex-col items-center justify-center gap-1 rounded-xl border transition-all relative px-3 py-2 min-w-[72px] md:min-w-[88px] ${
                  isActive
                    ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400 shadow-[0_0_0_1px_rgba(234,179,8,0.4)]'
                    : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
                }`}
                data-testid={`merkeskyting-tab-${t.id}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-5 w-5 md:h-6 md:w-6" />
                <span className="text-[11px] md:text-xs font-medium whitespace-nowrap leading-none">{t.label}</span>
              </button>
            );
          })}
        </ScrollableTabBar>
      </div>

      {/* Tab-innhold */}
      {activeTab === 'oversikt' && <OverviewPanel config={config} settings={settings} />}
      {activeTab === 'registrer' && settings?.enabled && <RegisterPanel config={config} settings={settings} resetSignal={registerResetSignal} />}
      {activeTab === 'resultater' && <ResultsPanel config={config} settings={settings} />}
      {activeTab === 'medlemmer' && <MembersPanel config={config} settings={settings} />}
      {activeTab === 'utdeling' && <AwardsPanel config={config} />}
      {activeTab === 'historikk' && <AuditLogPanel />}
      {activeTab === 'innstillinger' && <SettingsPanel config={config} settings={settings} onUpdated={setSettings} />}
      {activeTab === 'mine' && <MyProgressPanel config={config} />}
    </div>
  );
};

// ===================== OVERSIKT =====================
const OverviewPanel = ({ config, settings }) => {
  const [data, setData] = useState({ rows: [], badge_summary: [] });
  const [loading, setLoading] = useState(true);
  const [expandedMember, setExpandedMember] = useState(null); // member_id
  const [progressCache, setProgressCache] = useState({}); // {member_id: [progressions...]}
  const [loadingMember, setLoadingMember] = useState(null);
  const [downloadingAnnual, setDownloadingAnnual] = useState(false);
  const currentYear = new Date().getFullYear();
  const [reportYear, setReportYear] = useState(currentYear);
  const [filterYear, setFilterYear] = useState(''); // '' = alle år (kumulativt)

  const downloadAnnual = async () => {
    setDownloadingAnnual(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/report/annual?year=${reportYear}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `merkeskyting_arsrapport_${reportYear}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadingAnnual(false); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = filterYear ? `?year=${filterYear}` : '';
        const r = await axios.get(`${API_BASE}/api/merkeskyting/overview${params}`, { headers: { Authorization: `Bearer ${token}` } });
        setData(r.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [filterYear]);

  const toggleRow = async (memberId) => {
    if (expandedMember === memberId) {
      setExpandedMember(null);
      return;
    }
    setExpandedMember(memberId);
    if (!progressCache[memberId]) {
      setLoadingMember(memberId);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_BASE}/api/merkeskyting/member/${memberId}/progress`, { headers: { Authorization: `Bearer ${token}` } });
        setProgressCache((prev) => ({ ...prev, [memberId]: r.data.progressions || [] }));
      } catch (e) { console.error(e); }
      finally { setLoadingMember(null); }
    }
  };

  if (loading) return <div className="text-gray-400 p-4">Laster oversikt...</div>;

  return (
    <div className="space-y-4">
      {/* Klubbens totalt oppnådde/utdelte merker */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Klubbens merkestatus {filterYear && <span className="text-sm text-yellow-300">(kun {filterYear})</span>}
          </h2>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400">Filter år:</label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-xs" data-testid="overview-year-filter">
              <option value="">Alle år (kumulativt)</option>
              {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.badge_summary.map((b) => {
            const s = LEVEL_STYLES[b.level] || {};
            return (
              <div key={b.level} className="p-3 rounded-lg bg-gray-700/50 min-w-0" data-testid={`badge-summary-${b.level}`}>
                <div className="flex items-center gap-2 mb-1 min-w-0">
                  <span className="shrink-0"><BadgeRing level={b.level} size={28} /></span>
                  <span className="text-white text-sm font-semibold truncate">{s.label}</span>
                </div>
                <div className="text-xs text-gray-400">Oppnådd: <span className="text-white font-medium">{b.achieved}</span></div>
                <div className="text-xs text-gray-400">Utdelt: <span className="text-green-400 font-medium">{b.awarded}</span></div>
                {b.remaining > 0 && (
                  <div className="text-xs text-yellow-400 font-medium">Gjenstår: {b.remaining}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Alle medlemmer med progresjon */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-yellow-500" />
          Medlemmer med registrert merkeskyting ({data.rows.length})
        </h2>
        <p className="text-xs text-gray-400 mb-3">Klikk på en rad for å se fullstendig progresjon.</p>
        {data.rows.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Target className="w-10 h-10 mx-auto mb-2 text-gray-600" />
            <p>Ingen registrerte resultater ennå.</p>
            <p className="text-xs mt-1">Gå til <strong>Registrer</strong> for å legge inn første resultat.</p>
          </div>
        ) : (
          <div className="md:overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-xs md:text-sm" data-testid="overview-members-table">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="px-1 md:px-2 py-2 font-medium w-5 md:w-6"></th>
                  <th className="px-1.5 md:px-3 py-2 font-medium">Medlem</th>
                  <th className="px-1.5 md:px-3 py-2 font-medium">Øvelse<span className="hidden md:inline"> / Variant</span></th>
                  <th className="px-3 py-2 font-medium hidden md:table-cell">Sist fullført</th>
                  <th className="px-3 py-2 font-medium hidden md:table-cell">Aktivt</th>
                  <th className="px-1.5 md:px-3 py-2 font-medium">Progresjon</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {data.rows.map((row, i) => {
                  const isOpen = expandedMember === row.member_id;
                  const progs = progressCache[row.member_id];
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className="hover:bg-gray-700/40 cursor-pointer"
                        onClick={() => toggleRow(row.member_id)}
                        data-testid={`overview-row-${i}`}
                      >
                        <td className="px-1 md:px-2 py-2 text-yellow-400">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-1.5 md:px-3 py-2 text-white md:whitespace-nowrap break-words">{row.member_name || row.member_id.slice(0, 8)}</td>
                        <td className="px-1.5 md:px-3 py-2 text-gray-300">
                          <div className="flex flex-col min-w-0 break-words">
                            <span className="font-medium">{row.discipline_name}</span>
                            <span className="text-xs text-gray-500">{row.variant_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-400 hidden md:table-cell">
                          {row.last_achieved_level ? (LEVEL_STYLES[row.last_achieved_level]?.label || row.last_achieved_level) : '—'}
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell">
                          {row.active_level ? (
                            <div className="flex items-center gap-2">
                              <BadgeRing level={row.active_level} size={20} />
                              <span className="text-white text-xs">{row.active_level_name}</span>
                            </div>
                          ) : (
                            <span className="text-green-400 text-xs">Alle nivåer fullført</span>
                          )}
                        </td>
                        <td className="px-1.5 md:px-3 py-2">
                          {row.active_level ? (
                            <div className="flex items-center gap-1 mb-1 md:hidden">
                              <BadgeRing level={row.active_level} size={16} />
                              <span className="text-white text-[11px] leading-tight">{row.active_level_name}</span>
                            </div>
                          ) : (
                            <div className="text-green-400 text-[11px] mb-1 md:hidden">Alle fullført</div>
                          )}
                          <div className="flex items-center gap-1.5 md:gap-2 min-w-[64px] md:min-w-[120px]">
                            <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-yellow-500" style={{ width: `${row.active_percent}%` }} />
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{row.active_count}/10</span>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-900/40">
                          <td colSpan={6} className="px-1.5 md:px-3 py-3">
                            {loadingMember === row.member_id ? (
                              <div className="text-gray-400 text-sm">Laster progresjon...</div>
                            ) : progs && progs.length > 0 ? (
                              <div className="space-y-3">
                                {progs.map((p, j) => <ProgressionCard key={j} progression={p} defaultExpanded={true} compact />)}
                              </div>
                            ) : (
                              <div className="text-gray-400 text-sm">Ingen progresjonsdata.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TipCard>
        <strong>Tips til klubben:</strong> Aktiver bare de øvelsene klubben faktisk driver med. Lag gjerne dedikerte «merketreninger» der medlemmene får ekstra oppfølging på tellende serier – dette gjør ordningen mer motiverende.
      </TipCard>

      {/* Årsrapport-nedlasting */}
      <div className="bg-gray-800 rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-white font-semibold text-sm">Last ned årsrapport (PDF)</p>
          <p className="text-gray-400 text-xs">KPI-er, nivåfordeling, alle merker og topp 10 skyttere.</p>
        </div>
        <select value={reportYear} onChange={(e) => setReportYear(parseInt(e.target.value))} className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
          {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={downloadAnnual} disabled={downloadingAnnual} className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded-lg font-medium disabled:bg-gray-600" data-testid="download-annual-btn">
          {downloadingAnnual ? 'Genererer...' : 'Last ned PDF'}
        </button>
      </div>
    </div>
  );
};

// ===================== REGISTRER =====================
const RegisterPanel = ({ config, settings, resetSignal }) => {
  const toast = useToast();
  const { user } = useAuth();
  const [initialDraft] = useState(() => readDraft(user));
  const [resultDate, setResultDate] = useState(initialDraft?.resultDate || new Date().toISOString().slice(0, 10));
  const [members, setMembers] = useState([]);
  // [{member_id, member_name, discipline_key, variant_key, start_level, scores, status, statusLoading}]
  const [entries, setEntries] = useState(() => (initialDraft?.entries || []).map(e => ({ program: initialDraft?.program || '', ...e, status: null, statusLoading: true })));
  const lastProgramRef = React.useRef('');
  const [bulkSeries, setBulkSeries] = useState(4);
  const [draftRestored, setDraftRestored] = useState(() => (initialDraft?.entries || []).length > 0);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {awards_created, progressions}
  const [keypad, setKeypad] = useState(null); // {member_id, series_idx, member_name, current} | null
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);

  useEffect(() => {
    // Last inn medlemmer i klubben (aktive, ikke pauset), sortert alfabetisk
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const filtered = (r.data || []).filter(u => u.is_active !== false && !u.is_paused);
        filtered.sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', 'no', { sensitivity: 'base' }));
        setMembers(filtered);
      })
      .catch(() => {});
  }, []);

  const activeVariants = useMemo(() => {
    if (!config) return [];
    const active = new Set(settings?.active_variants || []);
    const list = [];
    for (const d of config.disciplines) {
      for (const v of d.variants) {
        const k = `${d.key}:${v.key}`;
        if (active.size === 0 || active.has(k)) {
          list.push({ discipline_key: d.key, discipline_name: d.name, variant_key: v.key, variant_name: v.name, unit_label: d.unit_label, requirements: v.requirements });
        }
      }
    }
    return list;
  }, [config, settings]);

  const patchEntry = (memberId, patch) => setEntries(prev => {
    const idx = prev.findIndex(e => e.member_id === memberId);
    const oldProgram = prev[idx]?.program || '';
    return prev.map((e, i) => {
      if (i === idx) return { ...e, ...patch };
      // Program-endring fyller automatisk videre til senere medlemmer som fortsatt har samme verdi
      if ('program' in patch && i > idx && (e.program || '') === oldProgram) return { ...e, program: patch.program };
      return e;
    });
  });

  const addMember = async (memberId) => {
    const m = members.find(x => x.id === memberId);
    if (!m || entries.some(e => e.member_id === memberId)) return;
    const prev = entries[entries.length - 1];
    setEntries(list => [...list, {
      member_id: memberId,
      member_name: m.full_name || m.email,
      discipline_key: prev?.discipline_key || (activeVariants.length === 1 ? activeVariants[0].discipline_key : ''),
      variant_key: prev?.variant_key || (activeVariants.length === 1 ? activeVariants[0].variant_key : ''),
      start_level: 'nybegynner',
      program: prev ? (prev.program || '') : lastProgramRef.current,
      scores: [''],
      status: null,
      statusLoading: true,
    }]);
    await fetchStatus(memberId);
  };

  const fetchStatus = async (memberId) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/members/${memberId}/variant-status`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(list => list.map(e => {
        if (e.member_id !== memberId) return e;
        const patch = { status: r.data, statusLoading: false };
        const last = r.data.last_variant;
        const prevWithVariant = [...list.slice(0, list.findIndex(x => x.member_id === memberId))].reverse().find(x => x.variant_key);
        if (!e.variant_key && prevWithVariant) {
          patch.discipline_key = prevWithVariant.discipline_key;
          patch.variant_key = prevWithVariant.variant_key;
        } else if (!e.variant_key && last && activeVariants.some(v => `${v.discipline_key}:${v.variant_key}` === last)) {
          const [d, v] = last.split(':');
          patch.discipline_key = d;
          patch.variant_key = v;
        }
        return { ...e, ...patch };
      }));
    } catch {
      patchEntry(memberId, { statusLoading: false });
    }
  };

  // Autolagre utkast lokalt på enheten + på serveren (alle enheter, autolagres som resultater etter 15 min)
  const serverLoadedRef = React.useRef(false);
  const lastPushedRef = React.useRef('');
  const [serverDraft, setServerDraft] = useState(null);
  const [syncState, setSyncState] = useState('idle');
  useEffect(() => {
    if (entries.length === 0) {
      localStorage.removeItem(draftKey(user));
    } else {
      const slim = entries.map(({ status, statusLoading, ...rest }) => rest);
      localStorage.setItem(draftKey(user), JSON.stringify({ resultDate, entries: slim, saved_at: Date.now() }));
    }
    if (!serverLoadedRef.current) return;
    const slim = entries.map(({ status, statusLoading, ...rest }) => rest);
    const sig = JSON.stringify([resultDate, slim]);
    if (sig === lastPushedRef.current) return;
    setSyncState('saving');
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        lastPushedRef.current = sig;
        const r = await axios.put(`${API_BASE}/api/merkeskyting/register-draft`, { result_date: resultDate, entries: slim }, { headers: { Authorization: `Bearer ${token}` } });
        setServerDraft(r.data.draft);
        setSyncState('idle');
      } catch { lastPushedRef.current = ''; setSyncState('error'); }
    }, 1200);
    return () => clearTimeout(t);
  }, [entries, resultDate, user]);

  // Hent økt fra serveren (kan være startet på en annen enhet)
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/register-draft`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r.data.draft;
        setServerDraft(d);
        const localAt = initialDraft?.saved_at || 0;
        if (d?.entries?.length && ((d.autosaved_result_ids || []).length > 0 || new Date(d.updated_at).getTime() >= localAt - 5000)) {
          setResultDate(d.result_date || new Date().toISOString().slice(0, 10));
          setEntries(d.entries.map(e => ({ ...e, status: null, statusLoading: true })));
          setDraftRestored(true);
          lastPushedRef.current = JSON.stringify([d.result_date || new Date().toISOString().slice(0, 10), d.entries]);
          d.entries.forEach(e => fetchStatus(e.member_id));
          return true;
        }
        return false;
      })
      .catch(() => false)
      .then((fromServer) => {
        serverLoadedRef.current = true;
        if (!fromServer) setEntries(e => (e.length ? [...e] : e));
      });
    // eslint-disable-next-line
  }, []);

  // Oppdater autolagringsstatus jevnlig
  useEffect(() => {
    if (entries.length === 0) return;
    const i = setInterval(() => {
      const token = localStorage.getItem('token');
      axios.get(`${API_BASE}/api/merkeskyting/register-draft`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setServerDraft(r.data.draft)).catch(() => {});
    }, 60000);
    return () => clearInterval(i);
  }, [entries.length]);

  const seriesCount = entries.reduce((n, e) => n + e.scores.filter(s => s !== '' && s !== null).length, 0);

  const addMemberRef = React.useRef(null);
  const startNextTeam = () => {
    setLastResult(null);
    setDraftRestored(false);
    setTimeout(() => {
      addMemberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addMemberRef.current?.querySelector('input')?.focus({ preventScroll: true });
    }, 50);
  };

  useEffect(() => {
    if (!resetSignal) return;
    if (entries.length === 0) setLastResult(null);
    startNextTeam();
    // eslint-disable-next-line
  }, [resetSignal]);

  // Sjekk startpunkt på nytt for gjenopprettede medlemmer
  useEffect(() => {
    (initialDraft?.entries || []).forEach(e => fetchStatus(e.member_id));
    // eslint-disable-next-line
  }, []);

  const clearDraft = () => {
    const hadAutosave = (serverDraft?.autosaved_result_ids || []).length > 0;
    setEntries([]);
    setDraftRestored(false);
    localStorage.removeItem(draftKey(user));
    lastPushedRef.current = JSON.stringify([resultDate, []]);
    const token = localStorage.getItem('token');
    axios.delete(`${API_BASE}/api/merkeskyting/register-draft`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    setServerDraft(null);
    if (hadAutosave) toast.success('Økten er avsluttet – de autolagrede seriene er beholdt under Resultater');
  };

  const updateScore = (memberId, seriesIdx, value) => {
    setEntries(list => list.map(e => e.member_id === memberId ? { ...e, scores: e.scores.map((s, i) => i === seriesIdx ? value : s) } : e));
  };

  const removeEntry = (memberId) => setEntries(list => list.filter(e => e.member_id !== memberId));

  const submit = async () => {
    if (entries.length === 0) { toast.error('Legg til minst ett medlem'); return; }
    const missing = entries.find(e => !e.variant_key);
    if (missing) { toast.error(`Velg øvelse for ${missing.member_name}`); return; }
    const empty = entries.find(e => !e.scores.some(s => s !== '' && s !== null));
    if (empty) { toast.error(`Fyll inn minst én serie for ${empty.member_name}`); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/api/merkeskyting/results`, {
        from_draft: true,
        result_date: resultDate,
        entries: entries.map(e => {
          const hasHistory = !!e.status?.variants?.[`${e.discipline_key}:${e.variant_key}`];
          return {
            member_id: e.member_id,
            member_name: e.member_name,
            discipline_key: e.discipline_key,
            variant_key: e.variant_key,
            program: (e.program || '').trim(),
            start_level: hasHistory ? 'nybegynner' : e.start_level,
            scores: e.scores.filter(s => s !== '' && s !== null),
          };
        }),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.inserted} resultater lagret`);
      setLastResult(r.data);
      lastProgramRef.current = entries[entries.length - 1]?.program || '';
      lastPushedRef.current = JSON.stringify([resultDate, []]);
      setServerDraft(null);
      setEntries([]);
      setDraftRestored(false);
      // Konfetti-feiring for oppnådde merker OG nye klubb-rekorder
      const shouldCelebrate = (r.data.awards_created?.length > 0) || (r.data.new_records?.length > 0);
      if (shouldCelebrate) {
        try {
          const celebrate = () => {
            confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 }, colors: ['#eab308', '#f97316', '#cbd5e1', '#b45309', '#fff'] });
            setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0 } }), 250);
            setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1 } }), 400);
          };
          celebrate();
        } catch {}
        if (r.data.new_records?.length > 0) {
          r.data.new_records.forEach((rec) => {
            toast.success(`🏆 NY KLUBBREKORD! ${rec.member_name}: ${rec.score} ${rec.unit_label} på ${rec.variant_name}${rec.previous_best ? ` (forrige: ${rec.previous_best})` : ''}`, { duration: 8000 });
          });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke lagre');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <CollapsibleInfoTip prefKey="register_progression" title="Slik virker sekvensiell progresjon (NSFs regel)">
        Skytteren jobber mot ETT aktivt nivå om gangen. Selv med gull-poeng teller resultatet bare for det aktive nivået. Du må ha <strong>10 godkjente serier</strong> på Nybegynner FØR Viderekommende starter, deretter 10 nye på Viderekommende osv. Totalt <strong>50 kvalifiserende serier</strong> for å nå Gull.
      </CollapsibleInfoTip>
      <CollapsibleInfoTip prefKey="register_howto" title="Slik registrerer du">
        Sett dato og legg til medlemmer. Hvert medlem får egen øvelse, eget program og eget antall serier – program fylles automatisk inn fra forrige medlem. Har medlemmet skutt øvelsen før, fortsetter Aktivlogg automatisk der de slapp – ellers velger du startpunkt (Nybegynner eller Bronse). Økten mellomlagres automatisk og kan hentes fram på alle enhetene dine. Glemmer du å trykke «Lagre resultater», lagres seriene automatisk etter 15 minutter uten endringer.
      </CollapsibleInfoTip>

      {draftRestored && entries.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-200 text-sm" data-testid="register-draft-banner">
          <span>
            Påbegynt økt hentet fram – {entries.length} medlem{entries.length === 1 ? '' : 'mer'}. Fortsett der du slapp.
            {(serverDraft?.autosaved_result_ids || []).length > 0 && (
              <span className="block text-xs text-green-300/80">Seriene er allerede autolagret. Trykk «Lagre resultater» når du er ferdig – ingenting blir lagret dobbelt.</span>
            )}
          </span>
          <button type="button" onClick={clearDraft} className="px-3 h-9 rounded-lg text-red-300 hover:bg-red-500/10 font-medium whitespace-nowrap" data-testid="register-draft-clear-btn">
            {(serverDraft?.autosaved_result_ids || []).length > 0 ? 'Avslutt økt' : 'Tøm utkast'}
          </button>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Dato</label>
            <input type="date" className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              value={resultDate} onChange={(e) => setResultDate(e.target.value)} data-testid="register-date-input" />
          </div>
          {entries.length > 0 && (
            <div className="flex flex-col sm:items-end justify-end gap-1">
              <SaveButton onClick={submit} saving={saving} testid="register-save-top-btn" className="w-full sm:w-auto" />
              <SyncStatus syncState={syncState} draft={serverDraft} />
            </div>
          )}
        </div>

        {/* Legg til medlem – søkbar dropdown + klassisk select */}
        <div className="space-y-2">
          <label className="block text-xs text-gray-400 mb-1">Legg til medlem</label>
          <div className="relative" data-testid="register-add-member-wrapper" ref={addMemberRef}>
            <input
              type="text"
              value={memberSearch}
              onChange={(e) => { setMemberSearch(e.target.value); setMemberDropdownOpen(true); }}
              onFocus={() => setMemberDropdownOpen(true)}
              onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
              placeholder="🔍 Skriv 2–3 tegn for rask søk etter medlem..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              data-testid="register-add-member-search"
            />
            {memberDropdownOpen && memberSearch.trim().length >= 2 && (
              <ul
                className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl"
                data-testid="register-member-dropdown"
              >
                {(() => {
                  const q = memberSearch.trim().toLowerCase();
                  const list = members
                    .filter(m => !entries.some(e => e.member_id === m.id))
                    .filter(m => (m.full_name || m.email || '').toLowerCase().includes(q));
                  if (list.length === 0) {
                    return <li className="px-3 py-2 text-gray-400 text-sm italic">Ingen medlemmer matcher "{memberSearch}"</li>;
                  }
                  return list.slice(0, 30).map(m => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { addMember(m.id); setMemberSearch(''); setMemberDropdownOpen(false); }}
                        className="w-full text-left px-3 py-2 text-white text-sm hover:bg-yellow-500 hover:text-gray-900 transition-colors"
                        data-testid={`register-member-option-${m.id}`}
                      >
                        {m.full_name || m.email}
                        {m.email && m.full_name && <span className="text-xs text-gray-400 ml-2">{m.email}</span>}
                      </button>
                    </li>
                  ));
                })()}
              </ul>
            )}
            {memberSearch.trim().length > 0 && memberSearch.trim().length < 2 && (
              <p className="text-xs text-gray-500 mt-1">Skriv minst 2 tegn for å søke...</p>
            )}
          </div>
          {/* Klassisk nedtrekksmeny (viser hele medlemslista i alfabetisk rekkefølge) */}
          <select
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm"
            value=""
            onChange={(e) => { if (e.target.value) addMember(e.target.value); }}
            data-testid="register-add-member-select"
          >
            <option value="">…eller velg fra hele listen</option>
            {members.filter(m => !entries.some(e => e.member_id === m.id)).map(m => (
              <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
            ))}
          </select>
        </div>

        {entries.length > 0 && (
          <div className="space-y-3" data-testid="register-entries-list">
            {entries.length >= 2 && (
              <BulkSeriesBar value={bulkSeries} onChange={setBulkSeries}
                onApply={() => setEntries(list => list.map(e => ({ ...e, scores: resizeScores(e.scores, bulkSeries) })))} />
            )}
            {entries.map((e) => (
              <MerkeRegisterEntryCard
                key={e.member_id}
                entry={e}
                activeVariants={activeVariants}
                onChange={(patch) => patchEntry(e.member_id, patch)}
                onRemove={() => removeEntry(e.member_id)}
                onScoreClick={(i) => setKeypad({ member_id: e.member_id, series_idx: i, member_name: e.member_name, current: e.scores[i] })}
              />
            ))}
          </div>
        )}

        <button
          onClick={submit}
          disabled={saving || entries.length === 0}
          className="w-full md:w-auto flex items-center justify-center gap-2 px-6 h-12 md:h-10 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 disabled:bg-gray-600 disabled:text-gray-400"
          data-testid="register-save-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Lagrer...' : 'Lagre resultater'}
        </button>
        {entries.length > 0 && (
          <p className="text-xs text-gray-400" data-testid="register-autosave-hint">
            Økten mellomlagres fortløpende og kan hentes fram på alle enhetene dine. Etter 15 minutter uten endringer lagres seriene automatisk som resultater.
          </p>
        )}
      </div>
      {entries.length > 0 && <div className="h-20" />}
      {entries.length > 0 && !keypad && (
        <MerkeRegisterSaveBar seriesCount={seriesCount} onSave={submit} saving={saving} syncState={syncState} draft={serverDraft} />
      )}

      {lastResult && (
        <div className="p-4 rounded-lg bg-gray-800 border border-gray-700 space-y-3" data-testid="last-result-summary">
          <div className="flex items-center justify-between">
            <p className="text-white font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />Lagret: {lastResult.inserted} serier</p>
            <button onClick={() => setLastResult(null)} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {entries.length === 0 && (
            <button type="button" onClick={startNextTeam}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-lg bg-yellow-500 text-gray-900 font-semibold hover:bg-yellow-400 touch-manipulation"
              data-testid="register-next-team-btn">
              <Plus className="w-5 h-5" />Registrer neste lag
            </button>
          )}
          {lastResult.awards_created?.length > 0 && (
            <div className="p-3 rounded bg-green-500/10 border border-green-500/40 text-green-100">
              <p className="font-semibold flex items-center gap-2"><Trophy className="w-4 h-4" />Nye merker oppnådd!</p>
              <ul className="mt-2 text-sm space-y-1">
                {lastResult.awards_created.map((a, i) => (
                  <li key={i}>• <strong>{a.member_name}</strong> – {LEVEL_STYLES[a.level]?.label} ({a.discipline_name}, {a.variant_name})</li>
                ))}
              </ul>
            </div>
          )}
          {lastResult.progressions?.map((p, i) => {
            const active = p.active_stage;
            if (!active) {
              return (
                <div key={i} className="p-3 rounded bg-yellow-500/10 border border-yellow-500/40 text-yellow-100 text-sm">
                  <strong>{p.member_name}</strong> har fullført alle nivåer på {p.variant_name}! 🏆
                </div>
              );
            }
            const remaining = 10 - active.count;
            return (
              <div key={i} className="p-3 rounded bg-gray-700/50 text-sm text-gray-200">
                <p><strong>{p.member_name}</strong> – {p.discipline_name} / {p.variant_name}</p>
                <p className="text-xs text-gray-400 mt-1">Aktivt nivå: <span className="text-white font-medium">{active.level_name}</span> – {active.count}/10 ({active.percent}%)</p>
                <p className="text-xs text-yellow-300 mt-1">Trenger <strong>{remaining} nye serier</strong> med minst <strong>{active.threshold} {p.unit_label}</strong> for å oppnå merket.</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Numerisk tastatur for rask inntasting */}
      <NumericKeypad
        open={!!keypad}
        onClose={() => setKeypad(null)}
        onSubmit={(num) => {
          if (keypad) {
            updateScore(keypad.member_id, keypad.series_idx, String(num));
          }
        }}
        initialValue={keypad?.current}
        title={keypad ? `${keypad.member_name} – Serie ${keypad.series_idx + 1}` : 'Poengsum'}
        min={0}
        max={100}
      />
    </div>
  );
};

// ===================== MEDLEMMER (detalj) =====================
const MembersPanel = ({ config, settings }) => {
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [progressions, setProgressions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingIndividual, setDownloadingIndividual] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);

  const selectedMember = members.find(m => m.id === selectedId);

  const downloadIndividual = async () => {
    if (!selectedId) return;
    setDownloadingIndividual(true);
    try {
      const token = localStorage.getItem('token');
      const member = members.find(m => m.id === selectedId);
      const r = await axios.get(`${API_BASE}/api/merkeskyting/report/individual/${selectedId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `merkeskyting_${(member?.full_name || 'medlem').replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadingIndividual(false); }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const sorted = [...(r.data || [])].sort((a, b) =>
          (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', 'no', { sensitivity: 'base' })
        );
        setMembers(sorted);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) { setProgressions([]); return; }
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/member/${selectedId}/progress`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProgressions(r.data.progressions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedId]);

  return (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-4">
        <label className="block text-xs text-gray-400 mb-1">Velg medlem</label>
        <div className="space-y-2">
          <div className="relative" data-testid="members-search-wrapper">
            <input
              type="text"
              value={memberSearch}
              onChange={(e) => { setMemberSearch(e.target.value); setMemberDropdownOpen(true); }}
              onFocus={() => setMemberDropdownOpen(true)}
              onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
              placeholder={selectedMember ? `Valgt: ${selectedMember.full_name || selectedMember.email}` : '🔍 Skriv 2–3 tegn for rask søk etter medlem...'}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              data-testid="members-search-input"
            />
            {memberDropdownOpen && memberSearch.trim().length >= 2 && (
              <ul
                className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl"
                data-testid="members-search-dropdown"
              >
                {(() => {
                  const q = memberSearch.trim().toLowerCase();
                  const list = members.filter(m => (m.full_name || m.email || '').toLowerCase().includes(q));
                  if (list.length === 0) {
                    return <li className="px-3 py-2 text-gray-400 text-sm italic">Ingen medlemmer matcher "{memberSearch}"</li>;
                  }
                  return list.slice(0, 30).map(m => (
                    <li key={m.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setSelectedId(m.id); setMemberSearch(''); setMemberDropdownOpen(false); }}
                        className="w-full text-left px-3 py-2 text-white text-sm hover:bg-yellow-500 hover:text-gray-900 transition-colors"
                        data-testid={`members-search-option-${m.id}`}
                      >
                        {m.full_name || m.email}
                        {m.email && m.full_name && <span className="text-xs text-gray-400 ml-2">{m.email}</span>}
                      </button>
                    </li>
                  ));
                })()}
              </ul>
            )}
            {memberSearch.trim().length > 0 && memberSearch.trim().length < 2 && (
              <p className="text-xs text-gray-500 mt-1">Skriv minst 2 tegn for å søke...</p>
            )}
          </div>
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            data-testid="members-select">
            <option value="">…eller velg fra hele listen</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
          </select>
        </div>
        {selectedId && (
          <button onClick={downloadIndividual} disabled={downloadingIndividual}
            className="mt-3 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded-lg font-medium text-sm disabled:bg-gray-600"
            data-testid="download-individual-btn">
            {downloadingIndividual ? 'Genererer...' : '⬇ Last ned statusrapport (PDF)'}
          </button>
        )}
      </div>

      {loading && <div className="text-gray-400 p-4">Laster...</div>}

      {selectedId && !loading && progressions.length === 0 && (
        <div className="text-gray-400 p-4 text-center">Ingen registrerte merkeresultater for dette medlemmet.</div>
      )}

      {progressions.map((p, i) => (
        <ProgressionCard key={i} progression={p} />
      ))}
    </div>
  );
};

// Bruker den delte komponenten fra components/MerkeskytingProgressionCard.js
const ProgressionCard = SharedProgressionCard;

// ===================== UTDELING =====================
const AwardsPanel = ({ config }) => {
  const toast = useToast();
  const [awards, setAwards] = useState([]);
  const [filter, setFilter] = useState('achieved'); // achieved | awarded
  const [filterYear, setFilterYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailMember, setDetailMember] = useState(null); // {id, name}
  const [detailProgs, setDetailProgs] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const currentYear = new Date().getFullYear();

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ status: filter });
      if (filterYear) params.set('year', filterYear);
      const r = await axios.get(`${API_BASE}/api/merkeskyting/awards?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const sorted = [...(r.data.awards || [])].sort((a, b) =>
        (a.member_name || '').localeCompare(b.member_name || '', 'no', { sensitivity: 'base' })
      );
      setAwards(sorted);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, filterYear]);

  const markDelivered = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/merkeskyting/awards/${id}/deliver`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Registrert som utdelt');
      load();
    } catch { toast.error('Feil ved registrering'); }
  };

  const unmarkDelivered = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/merkeskyting/awards/${id}/undeliver`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Angret utdeling');
      load();
    } catch { toast.error('Feil'); }
  };

  const openMemberDetail = async (memberId, memberName) => {
    setDetailMember({ id: memberId, name: memberName });
    setLoadingDetail(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/member/${memberId}/progress`, { headers: { Authorization: `Bearer ${token}` } });
      setDetailProgs(r.data.progressions || []);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  const downloadDiploma = async (awardId, memberName, level) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/merkeskyting/diploma/${awardId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `diplom_${level}_${(memberName || 'medlem').replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Kunne ikke laste ned diplom'); }
  };

  return (
    <div className="space-y-4">
      <InfoTip>
        Skill mellom <strong>oppnådd</strong> (10/10 serier registrert) og <strong>utdelt</strong> (klubben har overlevert merket til medlemmet). Bruk denne siden for å holde orden på klubbens bestilling og fysiske utdeling.
      </InfoTip>
      <div className="flex gap-2 items-center flex-wrap">
        <button onClick={() => setFilter('achieved')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === 'achieved' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-700 text-gray-300'}`} data-testid="awards-filter-achieved">Ikke utdelt</button>
        <button onClick={() => setFilter('awarded')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === 'awarded' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-700 text-gray-300'}`} data-testid="awards-filter-awarded">Utdelt</button>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-gray-400">År:</label>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-xs" data-testid="awards-year-filter">
            <option value="">Alle år</option>
            {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const r = await axios.get(`${API_BASE}/api/merkeskyting/diplomas/bulk?status=${filter}`, {
                  headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
                });
                const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `diplomer_masseutskrift_${filter}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(e.response?.status === 404 ? 'Ingen diplomer å skrive ut' : 'Feil ved masseutskrift');
              }
            }}
            className="text-xs px-3 py-1.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded hover:bg-purple-500/30 font-medium"
            data-testid="bulk-diplomas-btn"
          >
            🖨️ Masse-utskrift (PDF)
          </button>
        </div>
      </div>

      {loading && <div className="text-gray-400 p-4">Laster...</div>}
      {!loading && awards.length === 0 && <div className="text-gray-400 p-4 text-center">Ingen merker her.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {awards.map(a => (
          <div key={a.id} className="bg-gray-800 rounded-lg p-3 flex items-center gap-3" data-testid={`award-item-${a.id}`}>
            <BadgeRing level={a.level} size={40} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold truncate" data-testid={`award-member-${a.id}`}>{a.member_name || 'Ukjent medlem'}</p>
              <p className="text-yellow-300 text-sm font-medium truncate">{a.level_name} – {a.discipline_name}</p>
              <p className="text-gray-400 text-xs truncate">{a.variant_name}</p>
              <p className="text-gray-500 text-xs">Oppnådd: {fmtDateNo(a.achieved_at)}</p>
              {a.awarded_at && <p className="text-green-400 text-xs">Utdelt: {fmtDateNo(a.awarded_at)}</p>}
              <button
                onClick={() => openMemberDetail(a.member_id, a.member_name)}
                className="mt-1 inline-flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300"
                data-testid={`award-view-progress-${a.id}`}
              >
                <Eye className="w-3 h-3" /> Vis alle nivåer og resultater
              </button>
              <button
                onClick={() => downloadDiploma(a.id, a.member_name, a.level)}
                className="mt-1 ml-3 inline-flex items-center gap-1 text-xs text-yellow-300 hover:text-yellow-200"
                data-testid={`award-diploma-${a.id}`}
              >
                🎖️ Last ned diplom (PDF)
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {filter === 'achieved' ? (
                <button onClick={() => markDelivered(a.id)} className="text-xs px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/40 rounded hover:bg-green-500/30" data-testid={`deliver-btn-${a.id}`}>
                  Merk utdelt
                </button>
              ) : (
                <button onClick={() => unmarkDelivered(a.id)} className="text-xs px-3 py-1.5 bg-gray-700 text-gray-300 rounded hover:bg-gray-600">
                  Angre
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detalj-modal */}
      {detailMember && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDetailMember(null)}>
          <div className="bg-gray-900 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="award-detail-modal">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-white font-bold text-lg">{detailMember.name}</p>
                <p className="text-gray-400 text-xs">Fullstendig merkeprogresjon</p>
              </div>
              <button onClick={() => setDetailMember(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              {loadingDetail ? (
                <div className="text-gray-400">Laster...</div>
              ) : detailProgs.length === 0 ? (
                <div className="text-gray-400 text-center py-6">Ingen progresjonsdata for dette medlemmet.</div>
              ) : (
                detailProgs.map((p, i) => <ProgressionCard key={i} progression={p} defaultExpanded={true} compact />)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== INNSTILLINGER =====================
const SettingsPanel = ({ config, settings, onUpdated }) => {
  const toast = useToast();
  const [active, setActive] = useState(new Set(settings?.active_variants || []));
  const [logoUrl, setLogoUrl] = useState(settings?.logo_url || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleVariant = (key) => {
    const s = new Set(active);
    if (s.has(key)) s.delete(key); else s.add(key);
    setActive(s);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Maks 2 MB'); return; }
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_BASE}/api/merkeskyting/logo/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(r.data.logo_url);
      toast.success('Logo lastet opp');
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikke laste opp'); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm('Fjern Merkeskyting-logo? Diplomer vil da falle tilbake til klubbens hovedlogo (om den er satt).')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/merkeskyting/logo`, { headers: { Authorization: `Bearer ${token}` } });
      setLogoUrl('');
      toast.success('Logo fjernet');
    } catch { toast.error('Feil'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(`${API_BASE}/api/merkeskyting/settings`, {
        enabled: settings?.enabled !== false,
        active_variants: Array.from(active),
        eligibility_policy: settings?.eligibility_policy || 'all_sessions',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Innstillinger lagret');
      onUpdated?.(r.data.settings);
    } catch (e) { toast.error('Kunne ikke lagre'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <TipCard>
        <strong>Tips til klubben:</strong> Start med bare de øvelsene dere faktisk driver med. Dere kan alltid aktivere flere senere. Historiske resultater blir aldri slettet – de forblir tilgjengelige selv om en variant deaktiveres. <span className="text-yellow-300">Aktivering/deaktivering av hele modulen styres fra <em>Skyteleder → Innstillinger</em>.</span>
      </TipCard>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-bold mb-3">Aktive øvelser og varianter</h3>
        <div className="space-y-3">
          {config?.disciplines.map(d => (
            <div key={d.key} className="p-3 rounded-lg bg-gray-700/50">
              <p className="text-white font-semibold mb-2">{d.name} <span className="text-xs text-gray-400">({d.unit_label})</span></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {d.variants.map(v => {
                  const key = `${d.key}:${v.key}`;
                  return (
                    <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-700" data-testid={`variant-toggle-${key}`}>
                      <input type="checkbox" checked={active.has(key)} onChange={() => toggleVariant(key)} className="w-4 h-4" />
                      <span className="text-gray-200 text-sm">{v.name}</span>
                      <span className="text-xs text-gray-500 ml-auto">Gull {v.requirements.gull}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-bold mb-3">Klubbens Merkeskyting-logo</h3>
        <p className="text-gray-400 text-sm mb-3">Denne logoen vises øverst til venstre på diplomer og PDF-rapporter. Hvis ingen logo settes her, brukes klubbens hovedlogo.</p>
        {logoUrl ? (
          <div className="flex items-center gap-4 p-3 bg-gray-700/50 rounded-lg">
            <img src={logoUrl} alt="Merkeskyting-logo" className="h-16 max-w-[120px] object-contain bg-white rounded p-1" data-testid="settings-logo-preview" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Logo er satt</p>
              <p className="text-gray-400 text-xs truncate">Brukes på alle diplomer og PDF-eksporter.</p>
            </div>
            <button onClick={removeLogo} className="text-xs px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/40 rounded hover:bg-red-500/30" data-testid="settings-logo-remove">
              Fjern
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 border-dashed border-gray-600 hover:border-yellow-500 hover:bg-gray-700/30 transition-colors" data-testid="settings-logo-upload">
            <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={uploadLogo} className="hidden" disabled={uploadingLogo} />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{uploadingLogo ? 'Laster opp...' : 'Klikk for å laste opp logo'}</p>
              <p className="text-gray-400 text-xs mt-0.5">JPG eller PNG, maks 2 MB. Anbefalt: kvadratisk eller liggende format med gjennomsiktig bakgrunn.</p>
            </div>
          </label>
        )}
      </div>

      <button onClick={save} disabled={saving}
        className="flex items-center gap-2 px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 disabled:bg-gray-600"
        data-testid="settings-save-btn">
        <Save className="w-4 h-4" />
        {saving ? 'Lagrer...' : 'Lagre innstillinger'}
      </button>
    </div>
  );
};

// ===================== RESULTATER (liste med rediger/slett) =====================
const ResultsPanel = ({ config, settings }) => {
  const toast = useToast();
  const { isAdmin, isSuperAdmin } = useAuth();
  const [results, setResults] = useState([]);
  const [members, setMembers] = useState([]);
  const [awardsByMember, setAwardsByMember] = useState({}); // {member_id: [{level, level_name, discipline_name, variant_name}...]}
  const [loading, setLoading] = useState(true);
  const [filterMember, setFilterMember] = useState('');
  const [filterVariant, setFilterVariant] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const toggleSel = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterMember) params.set('member_id', filterMember);
      if (filterVariant) { const [d, v] = filterVariant.split(':'); params.set('discipline_key', d); params.set('variant_key', v); }
      if (filterYear) params.set('year', filterYear);
      const [rRes, rMem, rAwards] = await Promise.all([
        axios.get(`${API_BASE}/api/merkeskyting/results?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/api/merkeskyting/awards`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setResults(rRes.data.results || []);
      setSelected(new Set());
      const sortedMembers = [...(rMem.data || [])].sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', 'no', { sensitivity: 'base' }));
      setMembers(sortedMembers);
      // Bygg awards-map per medlem
      const map = {};
      for (const a of (rAwards.data.awards || [])) {
        if (a.status === 'reopened') continue;
        if (!map[a.member_id]) map[a.member_id] = [];
        map[a.member_id].push(a);
      }
      setAwardsByMember(map);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMember, filterVariant, filterYear]);

  const variantOptions = useMemo(() => {
    if (!config) return [];
    return config.disciplines.flatMap(d => d.variants.map(v => ({ key: `${d.key}:${v.key}`, label: `${d.name} – ${v.name}` })));
  }, [config]);

  const years = useMemo(() => {
    const set = new Set(results.map(r => r.result_year).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [results]);

  const doDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/merkeskyting/results/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resultat slettet – progresjon reberegnet');
      setConfirmDelete(null);
      load(); setRefreshKey(k => k + 1);
    } catch { toast.error('Kunne ikke slette'); }
  };

  return (
    <div className="space-y-4">
      <InfoTip>
        Her ser du alle registrerte serier. Rediger eller slett feilregistreringer – systemet reberegner automatisk medlemmets merkestatus.
      </InfoTip>

      <DiplomaManager key={`dm-${refreshKey}`} settings={settings} canEdit={isAdmin || isSuperAdmin} />

      {/* Filtre */}
      <div className="bg-gray-800 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
        <select value={filterMember} onChange={(e) => setFilterMember(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="results-filter-member">
          <option value="">Alle medlemmer</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
        </select>
        <select value={filterVariant} onChange={(e) => setFilterVariant(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="results-filter-variant">
          <option value="">Alle øvelser</option>
          {variantOptions.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" data-testid="results-filter-year">
          <option value="">Alle år</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Liste */}
      {loading ? <div className="text-gray-400 p-4">Laster...</div> : (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="text-gray-400 text-xs">{results.length} resultater</p>
            <button type="button" onClick={() => { setBulkMode(b => !b); setSelected(new Set()); }} data-testid="results-bulk-mode-btn"
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium touch-manipulation ${bulkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 hover:bg-yellow-500/25'}`}>
              {bulkMode ? <><X className="w-4 h-4" />Avslutt masseendring</> : <><CheckSquare className="w-4 h-4" />Masseendring</>}
            </button>
          </div>
          {selected.size > 0 && createPortal(
            <div className="fixed left-1/2 -translate-x-1/2 bottom-24 xl:bottom-6 w-[calc(100%-1.5rem)] max-w-3xl z-[60] shadow-2xl" data-testid="results-bulk-floating">
              <MerkeBulkBar selected={results.filter(r => selected.has(r.id))} onClear={() => setSelected(new Set())}
                onDone={() => { load(); setRefreshKey(k => k + 1); }} />
            </div>, document.body
          )}
          {results.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Target className="w-10 h-10 mx-auto mb-2 text-gray-600" />
              <p>Ingen resultater med disse filtrene.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 md:mx-0">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700 text-xs">
                    {bulkMode && <th className="pl-3 pr-1 py-2 w-8">
                      <input type="checkbox" className="w-4 h-4 accent-yellow-500 cursor-pointer" aria-label="Velg alle"
                        checked={results.length > 0 && selected.size === results.length}
                        ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < results.length; }}
                        onChange={(e) => setSelected(e.target.checked ? new Set(results.map(r => r.id)) : new Set())}
                        data-testid="results-select-all" />
                    </th>}
                    <th className="px-2 py-2">Dato</th>
                    <th className="px-2 py-2">Medlem</th>
                    <th className="px-2 py-2 hidden md:table-cell">Øvelse</th>
                    <th className="px-2 py-2">Serie</th>
                    <th className="px-2 py-2 text-right">Resultat</th>
                    <th className="px-2 py-2 hidden md:table-cell">Program</th>
                    <th className="px-2 py-2 hidden md:table-cell">Registrert av</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {results.map(r => {
                    const cfg = config?.disciplines?.find(d => d.key === r.discipline_key);
                    const variant = cfg?.variants?.find(v => v.key === r.variant_key);
                    const memberAwards = awardsByMember[r.member_id] || [];
                    return (
                      <tr key={r.id} className={selected.has(r.id) ? 'bg-yellow-500/10' : 'hover:bg-gray-700/40'} data-testid={`result-row-${r.id}`}>
                        {bulkMode && <td className="pl-3 pr-1 py-2">
                          <input type="checkbox" className="w-4 h-4 accent-yellow-500 cursor-pointer" checked={selected.has(r.id)}
                            onChange={() => toggleSel(r.id)} aria-label="Velg resultat" data-testid={`result-select-${r.id}`} />
                        </td>}
                        <td className="px-2 py-2 text-gray-300 whitespace-nowrap">{fmtDateNo(r.result_date)}</td>
                        <td className="px-2 py-2 text-white whitespace-nowrap max-w-[180px]">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">{r.member_name || r.member_id.slice(0, 8)}</span>
                            {memberAwards.length > 0 && (
                              <span className="inline-flex items-center gap-0.5">
                                {memberAwards.map((a) => (
                                  <BadgeIcon
                                    key={a.id}
                                    level={a.level}
                                    size={16}
                                    title={`${a.level_name} – ${a.discipline_name}, ${a.variant_name}${a.status === 'awarded' ? ' (utdelt)' : ' (oppnådd)'}`}
                                  />
                                ))}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-gray-400 hidden md:table-cell text-xs">{cfg?.name} – {variant?.name}</td>
                        <td className="px-2 py-2 text-gray-400 text-xs">S{r.series_order}</td>
                        <td className="px-2 py-2 text-right text-white font-semibold">{r.score}</td>
                        <td className="px-2 py-2 text-gray-400 hidden md:table-cell text-xs">{r.program || '—'}</td>
                        <td className="px-2 py-2 text-gray-500 hidden md:table-cell text-xs">{r.registered_by_name || '—'}</td>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <button onClick={() => setEditing(r)} className="p-1 text-yellow-400 hover:text-yellow-300" data-testid={`edit-result-${r.id}`}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => setConfirmDelete(r)} className="p-1 text-red-400 hover:text-red-300" data-testid={`delete-result-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit modal */}
      <MerkeChangeLog config={config} members={members} refreshSignal={refreshKey} onRestored={() => { load(); setRefreshKey(k => k + 1); }} />

      {editing && (
        <EditResultModal result={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setRefreshKey(k => k + 1); }} />
      )}

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <p className="text-white font-bold mb-2">Slette resultat?</p>
            <p className="text-gray-300 text-sm mb-4">
              Sletter serie {confirmDelete.series_order} ({confirmDelete.score} poeng) for {confirmDelete.member_name} datert {fmtDateNo(confirmDelete.result_date)}. Merkeprogresjonen reberegnes automatisk.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded">Avbryt</button>
              <button onClick={() => doDelete(confirmDelete.id)} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-400" data-testid="confirm-delete-btn">Slett</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EditResultModal = ({ result, onClose, onSaved }) => {
  const toast = useToast();
  const [score, setScore] = useState(String(Math.round(result.score ?? 0)));
  const [resultDate, setResultDate] = useState(result.result_date);
  const [program, setProgram] = useState(result.program || '');
  const [notes, setNotes] = useState(result.notes || '');
  const [saving, setSaving] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/merkeskyting/results/${result.id}`, {
        score: parseInt(score, 10),
        result_date: resultDate,
        program,
        notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resultat oppdatert – progresjon reberegnet');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Feil ved oppdatering'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-result-modal">
        <div className="flex items-center justify-between mb-4">
          <p className="text-white font-bold">Rediger resultat</p>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Poengsum (hele tall)</label>
            <button
              type="button"
              onClick={() => setShowKeypad(true)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-3 text-white text-left text-xl font-semibold hover:bg-gray-600"
              data-testid="edit-score-input"
            >
              {score || <span className="text-gray-500">Klikk for å taste inn</span>}
            </button>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Dato</label>
            <input type="date" value={resultDate} onChange={(e) => setResultDate(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Program</label>
            <input type="text" value={program} onChange={(e) => setProgram(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notater</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" rows="2" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded">Avbryt</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-yellow-500 text-gray-900 rounded font-medium hover:bg-yellow-400 disabled:bg-gray-600" data-testid="edit-save-btn">
            {saving ? 'Lagrer...' : 'Lagre'}
          </button>
        </div>
      </div>
      <NumericKeypad
        open={showKeypad}
        onClose={() => setShowKeypad(false)}
        onSubmit={(num) => setScore(String(num))}
        initialValue={score}
        title="Rediger poengsum"
        min={0}
        max={100}
      />
    </div>
  );
};

// ===================== HISTORIKK (audit log) =====================
const AuditLogPanel = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/audit-logs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLogs(r.data.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actionLabel = (a) => ({
    register: { text: 'Registrerte serier', color: 'text-blue-400', icon: '📝' },
    update: { text: 'Endret resultat', color: 'text-yellow-400', icon: '✏️' },
    delete: { text: 'Slettet resultat', color: 'text-red-400', icon: '🗑️' },
    deliver: { text: 'Merket utdelt', color: 'text-green-400', icon: '🏆' },
    undeliver: { text: 'Angret utdeling', color: 'text-orange-400', icon: '↩️' },
    settings_update: { text: 'Endret innstillinger', color: 'text-purple-400', icon: '⚙️' },
    diploma_update: { text: 'Redigerte diplom', color: 'text-amber-300', icon: '🎖️' },
    diploma_defaults_update: { text: 'Endret diplom-standard', color: 'text-purple-400', icon: '🎖️' },
    restore: { text: 'Angret endring', color: 'text-purple-300', icon: '♻️' },
  })[a] || { text: a, color: 'text-gray-400', icon: '•' };

  const formatDetail = (log) => {
    const d = log.details || {};
    if (log.action === 'register') return `${d.series_count} serier på ${d.variant_key} ${d.awards_created?.length ? `– ${d.awards_created.length} nye merker oppnådd!` : ''}`;
    if (log.action === 'update') return `${d.member_name}: ${d.old_score} → ${d.new_score}`;
    if (log.action === 'delete') return `${d.member_name}: serie ${d.score} (${d.result_date}) slettet`;
    if (log.action === 'deliver') return `${d.member_name}: ${d.level_name}-merke utdelt`;
    if (log.action === 'undeliver') return `${d.member_name}: ${d.level_name} tilbake til "ikke utdelt"`;
    if (log.action === 'diploma_update') {
      const c = d.changes || {};
      const parts = [];
      if (c.achieved_at) parts.push(`dato ${fmtDateNo(c.achieved_at.old)} → ${fmtDateNo(c.achieved_at.new)}`);
      if (c.logo_mode) parts.push(c.logo_mode.new === 'logo' ? 'viser logo' : 'viser klubbnavn');
      if (c.show_footer) parts.push(c.show_footer.new ? 'Aktivlogg-linje på' : 'Aktivlogg-linje av');
      return `${d.member_name}: ${d.level_name}-diplom – ${parts.join(', ')}`;
    }
    if (log.action === 'diploma_defaults_update') return `Standard: ${d.logo_mode === 'logo' ? 'logo' : 'klubbnavn'}, Aktivlogg-linje ${d.show_footer ? 'på' : 'av'}`;
    if (log.action === 'restore') return d.summary || '';
    if (log.action === 'settings_update') return `Modul ${d.enabled ? 'aktivert' : 'deaktivert'}, ${d.active_variants_count} varianter valgt`;
    return '';
  };

  if (loading) return <div className="text-gray-400 p-4">Laster historikk...</div>;

  return (
    <div className="space-y-4">
      <InfoTip>
        Historikken viser alle endringer i Merkeskyting: hvem registrerte hva, endringer, sletting og utdelinger. Vises kronologisk med nyeste øverst.
      </InfoTip>
      <div className="bg-gray-800 rounded-lg p-4">
        {logs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Clock className="w-10 h-10 mx-auto mb-2 text-gray-600" />
            <p>Ingen hendelser registrert ennå.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map(l => {
              const meta = actionLabel(l.action);
              return (
                <div key={l.id} className="p-3 rounded-lg bg-gray-700/50 border border-gray-700 flex items-start gap-3" data-testid={`audit-log-${l.id}`}>
                  <span className="text-xl flex-shrink-0">{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className={`font-semibold text-sm ${meta.color}`}>{meta.text}</span>
                      <span className="text-gray-400 text-xs">av <strong className="text-gray-200">{l.user_name}</strong></span>
                      <span className="text-gray-500 text-xs">{new Date(l.created_at).toLocaleString('no-NO')}</span>
                    </div>
                    <p className="text-gray-300 text-sm mt-0.5">{formatDetail(l)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== MINE MERKER (medlem) =====================
const MyProgressPanel = ({ config }) => {
  const [progressions, setProgressions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/my-progress`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProgressions(r.data.progressions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-400 p-4">Laster...</div>;
  if (progressions.length === 0) return (
    <div className="text-center py-8 text-gray-400" data-testid="no-my-progress">
      <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-600" />
      <p>Du har ikke registrert noen merkeskytingsresultater ennå.</p>
      <p className="text-sm mt-2">Snakk med skyteleder i klubben din for å komme i gang!</p>
    </div>
  );
  return (
    <div className="space-y-4">
      <InfoTip>
        Her ser du progresjonen din. Hvert nivå krever <strong>10 godkjente resultater</strong>. Det er ingen tidsfrist – resultatene teller på tvers av år.
      </InfoTip>
      {progressions.map((p, i) => <ProgressionCard key={i} progression={p} />)}
    </div>
  );
};

export default Merkeskyting;
